import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Paper,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PrintIcon from '@mui/icons-material/Print';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import { RingSoundOption } from '../hooks/useLocalSettings';
import PrintingSettingsTab from '../../quickSale/settings/PrintingSettingsTab';
import salesService from '../../../../services/salesService';

interface DefinedPrinter {
  id: string;
  name: string;
}

interface InventoryCategory {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
  createdAt: string;
  branchesId?: string | null;
}

export interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  themeColor: string;
  phoneRingSoundEnabled: boolean;
  setPhoneRingSoundEnabled: (enabled: boolean) => void;
  selectedRingSound: string;
  setSelectedRingSound: (sound: string) => void;
  ringSoundOptions: RingSoundOption[];
  playRingSound: () => void;
  stopRingSound: () => void;
  incomingCallModalEnabled: boolean;
  setIncomingCallModalEnabled: (enabled: boolean) => void;
  screenId?: number | null;
  localServiceUrl?: string | null;
  onPrintingSettingsSaved?: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
  open,
  onClose,
  themeColor,
  phoneRingSoundEnabled,
  setPhoneRingSoundEnabled,
  selectedRingSound,
  setSelectedRingSound,
  ringSoundOptions,
  playRingSound,
  stopRingSound,
  incomingCallModalEnabled,
  setIncomingCallModalEnabled,
  screenId,
  localServiceUrl,
  onPrintingSettingsSaved,
}) => {
  const { t } = useTranslation('salesSystem');
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Yazdırma state'leri
  const [printingMode, setPrintingMode] = useState<'single' | 'category'>('single');
  const [defaultReceiptPrinter, setDefaultReceiptPrinter] = useState<DefinedPrinter | null>(null);
  const [definedPrinters, setDefinedPrinters] = useState<DefinedPrinter[]>([]);
  const [categoryPrinterAssignments, setCategoryPrinterAssignments] = useState<{ [categoryId: string]: string | null }>({});
  const [adisyonPrinter, setAdisyonPrinter] = useState<DefinedPrinter | null>(null);
  const [localPrinters, setLocalPrinters] = useState<DefinedPrinter[]>([]);
  const [loadingLocalPrinters, setLoadingLocalPrinters] = useState(false);
  const [localPrintersError, setLocalPrintersError] = useState<string | null>(null);
  const [printTestLoading, setPrintTestLoading] = useState<string | null>(null);
  const [allCategories, setAllCategories] = useState<InventoryCategory[]>([]);
  // QuickSale ekranının `StockVisibilityJson` içinden çıkartılan, BU ekrana
  // atanmış kategori ID'leri. Yazıcı atama tablosu sadece bu kategorilere
  // filtrelenir — şirketteki tüm kategoriler değil.
  const [screenAssignedCategoryIds, setScreenAssignedCategoryIds] = useState<string[]>([]);
  const [printingSettingsLoaded, setPrintingSettingsLoaded] = useState(false);
  const [savingPrintSettings, setSavingPrintSettings] = useState(false);
  const [receiptSettingsModalOpen, setReceiptSettingsModalOpen] = useState(false);
  const [receiptSettings, setReceiptSettings] = useState<any>({});

  const lsUrl = localServiceUrl || 'http://localhost:3003';

  const fetchLocalPrinters = useCallback(async () => {
    setLoadingLocalPrinters(true);
    setLocalPrintersError(null);
    setLocalPrinters([]);
    try {
      const response = await fetch(`${lsUrl}/api/yazicilariGetir`);
      if (!response.ok) {
        let errorMessage = t('orderManagement.settings.snackbar.localServiceUnreachable', { code: response.status });
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          errorMessage = `${errorMessage}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
      const printersFromService: Array<{ id: string; name: string }> = await response.json();
      setLocalPrinters(printersFromService.map(p => ({ id: p.id, name: p.name })));
    } catch (err: any) {
      console.error('[SettingsModal] Yerel yazıcılar alınırken hata:', err);
      if (lsUrl !== 'http://localhost:3003') {
        try {
          const fallbackResponse = await fetch('http://localhost:3003/api/yazicilariGetir');
          if (fallbackResponse.ok) {
            const printersFromService: Array<{ id: string; name: string }> = await fallbackResponse.json();
            setLocalPrinters(printersFromService.map(p => ({ id: p.id, name: p.name })));
            return;
          }
        } catch {}
      }
      setLocalPrintersError(err.message || t('orderManagement.settings.snackbar.localPrintersError'));
    } finally {
      setLoadingLocalPrinters(false);
    }
  }, [lsUrl, t]);

  // Ekran verileri yükle
  useEffect(() => {
    if (!open || !screenId || printingSettingsLoaded) return;

    const loadData = async () => {
      try {
        const screenData = await salesService.getQuickSaleScreenById(screenId);
        if (screenData?.PrintingSettingsJson) {
          try {
            const ps = JSON.parse(screenData.PrintingSettingsJson);
            setPrintingMode(ps.printingMode || 'single');
            setDefaultReceiptPrinter(ps.defaultReceiptPrinter || null);
            setDefinedPrinters(ps.definedPrinters || []);
            setCategoryPrinterAssignments(ps.categoryPrinterAssignments || {});
            setAdisyonPrinter(ps.adisyonPrinter || null);
            setReceiptSettings(ps.receiptDisplaySettings || {});
          } catch (e) {
            console.error('[SettingsModal] PrintingSettingsJson parse hatası:', e);
          }
        }

        // QuickSale ekranına atanmış kategori ID'lerini çıkar. Yapı:
        //   stockVisibility.categories = { [warehouseId]: [categoryId, ...] }
        // Tüm depoların kategorilerini düzleştirip benzersizleştiriyoruz.
        if (screenData?.StockVisibilityJson) {
          try {
            const sv = JSON.parse(screenData.StockVisibilityJson);
            const ids: string[] = [];
            const categoriesMap = sv?.categories || {};
            Object.values(categoriesMap).forEach((cats: any) => {
              if (Array.isArray(cats)) {
                cats.forEach((cid: string) => {
                  if (typeof cid === 'string' && !ids.includes(cid)) ids.push(cid);
                });
              }
            });
            setScreenAssignedCategoryIds(ids);
            console.log('[SettingsModal] Ekrana atanmış kategori ID sayısı:', ids.length);
          } catch (e) {
            console.error('[SettingsModal] StockVisibilityJson parse hatası:', e);
            setScreenAssignedCategoryIds([]);
          }
        } else {
          setScreenAssignedCategoryIds([]);
        }

        setPrintingSettingsLoaded(true);
      } catch (err) {
        console.error('[SettingsModal] Ekran verileri yüklenemedi:', err);
      }
    };

    loadData();
  }, [open, screenId, printingSettingsLoaded]);

  // Kategorileri yükle
  useEffect(() => {
    if (!open || allCategories.length > 0) return;
    const loadCategories = async () => {
      try {
        const { default: accountingService } = await import('../../../../services/accountingService');
        const cats = await accountingService.getCategories(false);
        setAllCategories(cats.map((cat: any) => ({
          id: cat.id?.toString() || cat.Id?.toString() || '',
          name: cat.name || cat.Name || 'İsimsiz Kategori',
          parentId: cat.parentId || cat.ParentId || null,
          level: cat.level || cat.Level || 0,
          createdAt: cat.createdAt || cat.CreatedAt || '',
          branchesId: cat.branchesId || cat.BranchesId || null,
        })).filter((c: any) => c.id));
      } catch (err) {
        console.error('[SettingsModal] Kategoriler yüklenemedi:', err);
      }
    };
    loadCategories();
  }, [open, allCategories.length]);

  // Yazdırma tabı açıldığında yazıcıları yükle
  useEffect(() => {
    if (open && activeTab === 1 && localPrinters.length === 0 && !loadingLocalPrinters) {
      fetchLocalPrinters();
    }
  }, [open, activeTab, localPrinters.length, loadingLocalPrinters, fetchLocalPrinters]);

  // Modal kapanınca state sıfırla
  useEffect(() => {
    if (!open) {
      setPrintingSettingsLoaded(false);
      setActiveTab(0);
    }
  }, [open]);

  // Yazıcı atama tablosunda **sadece BU paket ekranının bağlı olduğu QuickSale
  // ekranına atanmış kategoriler** listelenir. Bunun iki sebebi var:
  //   1) Telefon paket siparişlerinin kategori-bazlı yazdırması ürünün stok
  //      kartındaki orijinal `CategoryId`'ye değil, QuickSale ekranındaki
  //      ürün-kategori eşleştirmesine (`StockVisibilityJson.products`) bakar.
  //      Dolayısıyla atanabilecek kategoriler de aynı ekrana ait olmalıdır;
  //      ekran dışı kategorilere yapılan atamalar zaten karşılığı olmayan
  //      ürünler tarafından kullanılmaz.
  //   2) Şirketteki tüm kategoriler listelenince kullanıcı yanlış kategoriye
  //      yazıcı atayıp ürünün varsayılan (adisyon) yazıcıya düşmesine sebep
  //      oluyordu.
  //
  // `StockVisibilityJson` parse'ı henüz yüklenmediyse veya boşsa fallback
  // olarak hiçbir kategori listelemiyoruz — kullanıcının yanlış kategoriye
  // atama yapmasını engelliyor.
  const uniqueCategoryIds = useMemo(() => {
    return screenAssignedCategoryIds;
  }, [screenAssignedCategoryIds]);

  const categoriesToListForPrinting = useMemo(() => {
    const assignedSet = new Set(screenAssignedCategoryIds);
    return allCategories
      .filter(c => assignedSet.has(c.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allCategories, screenAssignedCategoryIds]);

  const handleTestRingSound = () => {
    playRingSound();
    setTimeout(() => stopRingSound(), 2000);
  };

  const handlePrintingModeChange = (_event: React.ChangeEvent<HTMLInputElement>, newMode: string) => {
    setPrintingMode(newMode as 'single' | 'category');
  };

  const handleSetDefaultPrinter = (printer: DefinedPrinter) => {
    setDefaultReceiptPrinter(printer);
  };

  const handleRemoveDefaultPrinter = () => {
    setDefaultReceiptPrinter(null);
  };

  const handleCategoryPrinterChange = (categoryId: string, printerId: string | null) => {
    setCategoryPrinterAssignments(prev => ({ ...prev, [categoryId]: printerId }));
  };

  const handlePrintTestPage = async (printerId: string, _printerName: string) => {
    setPrintTestLoading(printerId);
    try {
      const testUrl = `${lsUrl}/api/sinamaSayfasiYazdir`;
      let response: Response;
      try {
        response = await fetch(testUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ printerName: printerId }),
        });
      } catch {
        if (lsUrl !== 'http://localhost:3003') {
          response = await fetch('http://localhost:3003/api/sinamaSayfasiYazdir', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ printerName: printerId }),
          });
        } else {
          throw new Error(t('orderManagement.settings.snackbar.localServiceConnectionError'));
        }
      }
      if (response!.ok) {
        setSnackbar({ open: true, message: t('orderManagement.settings.snackbar.testPagePrinted'), severity: 'success' });
      } else {
        setSnackbar({ open: true, message: t('orderManagement.settings.snackbar.testPageFailed'), severity: 'error' });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: t('orderManagement.settings.snackbar.testError', { message: err.message }), severity: 'error' });
    } finally {
      setPrintTestLoading(null);
    }
  };

  const handleSavePrintingSettings = async () => {
    if (!screenId) {
      setSnackbar({ open: true, message: t('orderManagement.settings.snackbar.screenInfoMissing'), severity: 'error' });
      return;
    }

    setSavingPrintSettings(true);
    try {
      const printingSettings = {
        printingMode,
        defaultReceiptPrinter: printingMode === 'single' ? defaultReceiptPrinter : null,
        definedPrinters: printingMode === 'category' ? definedPrinters : [],
        categoryPrinterAssignments: printingMode === 'category' ? categoryPrinterAssignments : {},
        receiptDisplaySettings: receiptSettings,
        adisyonPrinter: printingMode === 'category' ? adisyonPrinter : null,
      };

      await salesService.updateQuickSaleScreen({
        Id: screenId,
        PrintingSettingsJson: JSON.stringify(printingSettings),
      });

      setSnackbar({ open: true, message: t('orderManagement.settings.snackbar.settingsSaved'), severity: 'success' });
      onPrintingSettingsSaved?.();
    } catch (err: any) {
      console.error('[SettingsModal] Yazdırma ayarları kaydedilemedi:', err);
      setSnackbar({ open: true, message: t('orderManagement.settings.snackbar.saveFailed', { message: err.message || 'Bilinmeyen hata' }), severity: 'error' });
    } finally {
      setSavingPrintSettings(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: themeColor, color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon />
          {t('orderManagement.settings.title')}
        </DialogTitle>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="fullWidth"
          >
            <Tab icon={<SettingsIcon />} label={t('orderManagement.settings.tabs.general')} iconPosition="start" />
            <Tab icon={<PrintIcon />} label={t('orderManagement.settings.tabs.printing')} iconPosition="start" />
          </Tabs>
        </Box>
        <DialogContent sx={{ pt: 2, minHeight: 400 }}>

          {/* TAB 0: Genel Ayarlar */}
          {activeTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {phoneRingSoundEnabled ? <VolumeUpIcon color="primary" /> : <VolumeOffIcon color="disabled" />}
                    <Typography variant="subtitle1" fontWeight="bold">
                      {t('orderManagement.settings.general.phoneRing.title')}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={phoneRingSoundEnabled}
                        onChange={(e) => setPhoneRingSoundEnabled(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={phoneRingSoundEnabled ? t('orderManagement.settings.general.phoneRing.on') : t('orderManagement.settings.general.phoneRing.off')}
                    labelPlacement="start"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('orderManagement.settings.general.phoneRing.description')}
                </Typography>
                {phoneRingSoundEnabled && (
                  <Box>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('orderManagement.settings.general.phoneRing.selectSound')}</InputLabel>
                      <Select
                        value={selectedRingSound}
                        label={t('orderManagement.settings.general.phoneRing.selectSound')}
                        onChange={(e: SelectChangeEvent) => setSelectedRingSound(e.target.value)}
                      >
                        {ringSoundOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button variant="outlined" size="small" onClick={handleTestRingSound} sx={{ mt: 1 }}>
                      {t('orderManagement.settings.general.phoneRing.test')}
                    </Button>
                  </Box>
                )}
              </Paper>

              <Paper elevation={1} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <OpenInNewIcon color={incomingCallModalEnabled ? "primary" : "disabled"} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      {t('orderManagement.settings.general.incomingCallPopup.title')}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={incomingCallModalEnabled}
                        onChange={(e) => setIncomingCallModalEnabled(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={incomingCallModalEnabled ? t('orderManagement.settings.general.incomingCallPopup.on') : t('orderManagement.settings.general.incomingCallPopup.off')}
                    labelPlacement="start"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t('orderManagement.settings.general.incomingCallPopup.description')}
                </Typography>
              </Paper>
            </Box>
          )}

          {/* TAB 1: Yazdırma Ayarları */}
          {activeTab === 1 && (
            <Box>
              {!screenId ? (
                <Alert severity="warning">{t('orderManagement.settings.printing.screenInfoMissing')}</Alert>
              ) : !printingSettingsLoaded ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
                  <CircularProgress size={32} />
                  <Typography sx={{ ml: 2 }}>{t('orderManagement.settings.printing.loadingPrinters')}</Typography>
                </Box>
              ) : (
                <>
                  <PrintingSettingsTab
                    printingMode={printingMode}
                    defaultReceiptPrinter={defaultReceiptPrinter}
                    definedPrinters={definedPrinters}
                    categoryPrinterAssignments={categoryPrinterAssignments}
                    uniqueCategoryIds={uniqueCategoryIds}
                    categoriesToListForPrinting={categoriesToListForPrinting}
                    localPrinters={localPrinters}
                    loadingLocalPrinters={loadingLocalPrinters}
                    localPrintersError={localPrintersError}
                    onPrintingModeChange={handlePrintingModeChange}
                    onSetDefaultPrinter={handleSetDefaultPrinter}
                    onRemoveDefaultPrinter={handleRemoveDefaultPrinter}
                    onCategoryPrinterChange={handleCategoryPrinterChange}
                    onRefreshLocalPrinters={fetchLocalPrinters}
                    onPrintTestPage={handlePrintTestPage}
                    printTestLoading={printTestLoading}
                    onOpenReceiptSettingsModal={() => setReceiptSettingsModalOpen(true)}
                    adisyonPrinter={adisyonPrinter}
                    onAdisyonPrinterChange={(printer) => setAdisyonPrinter(printer)}
                    isRestaurantMode={false}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={savingPrintSettings ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                      onClick={handleSavePrintingSettings}
                      disabled={savingPrintSettings}
                    >
                      {savingPrintSettings ? t('orderManagement.settings.printing.saving') : t('orderManagement.settings.printing.saveButton')}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}

        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={onClose} color="primary">
            {t('orderManagement.settings.actions.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SettingsModal;
